"use client";

import * as React from "react";
import AccountsTable from "./AccountsTable";
import type { Account } from "../../data/accounts";

type VatMode = "NORMAL" | "PAUSCHAL" | "NICHT_STEUERPFLICHTIG";
type AccountType = "Asset" | "Liability" | "Equity" | "Revenue" | "Expense";

type LearningSignal = "SUGGESTION_SHOWN" | "SUGGESTION_ACCEPTED" | "SUGGESTION_CORRECTED" | "SUGGESTION_REJECTED" | "ACCOUNT_CREATED";

function inferNumberPrefix(type: AccountType): string {
  if (type === "Asset") return "1";
  if (type === "Liability" || type === "Equity") return "2";
  if (type === "Revenue") return "3";
  return "6";
}

function nextSuggestedNumber(data: Account[], type: AccountType): string {
  const prefix = inferNumberPrefix(type);
  const nums = data
    .filter((a) => a.type === type && a.number.startsWith(prefix))
    .map((a) => Number(a.number))
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return `${prefix}000`;
  return String(Math.max(...nums) + 1);
}

export default function AccountsScreen({ data }: { data: Account[] }) {
  const [accounts, setAccounts] = React.useState(data);
  const [q, setQ] = React.useState("");
  const [vatMode, setVatMode] = React.useState<VatMode>("NORMAL");
  const [note, setNote] = React.useState<string | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [createBusy, setCreateBusy] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState({
    number: "",
    name: "",
    type: "Expense" as AccountType,
    group: "",
    subgroup: ""
  });

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return accounts;
    return accounts.filter((r) =>
      [r.number, r.name, r.type, r.group].join(" ").toLowerCase().includes(s)
    );
  }, [accounts, q]);

  const suggested = React.useMemo(() => {
    const type = accountForm.type;
    const number = nextSuggestedNumber(accounts, type);
    const group = accounts.find((a) => a.type === type)?.group || "";
    return { number, group };
  }, [accounts, accountForm.type]);

  const groupOptions = React.useMemo(() => {
    const uniq = new Set(
      accounts
        .map((a) => (a.group || "").trim())
        .filter((g) => g.length > 0)
    );
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, "de-CH"));
  }, [accounts]);

  const subgroupOptions = React.useMemo(() => {
    const uniq = new Set(
      accounts
        .map((a) => String(a.group || "").split("·").map((x) => x.trim()).filter(Boolean).slice(-1)[0] || "")
        .filter((g) => g.length > 0)
    );
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, "de-CH"));
  }, [accounts]);

  async function sendLearningSignal(signal: LearningSignal, extra?: Record<string, unknown>) {
    try {
      await fetch("/api/accounts/learning", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signal,
          draft: accountForm,
          suggestion: suggested,
          ...extra
        })
      });
    } catch {
      // Learning feedback should not break UX flow.
    }
  }

  React.useEffect(() => {
    if (!showCreate) return;
    sendLearningSignal("SUGGESTION_SHOWN");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate, accountForm.type]);

  function applySuggestion() {
    setAccountForm((s) => ({
      ...s,
      number: suggested.number,
      group: suggested.group
    }));
    sendLearningSignal("SUGGESTION_ACCEPTED");
    setNote("🤖 Vorschlag übernommen.");
  }

  async function createAccount() {
    const number = accountForm.number.trim();
    const name = accountForm.name.trim();
    if (!number || !name) {
      setNote("❌ Konto-Nr. und Name sind erforderlich.");
      return;
    }
    setCreateBusy(true);
    try {
      const corrected = number !== suggested.number || (accountForm.group || "").trim() !== (suggested.group || "").trim();
      const createdPayload = { ...accountForm, number, name };
      const createdRes = await fetch("/api/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createdPayload)
      });
      const createdJson = await createdRes.json();
      if (!createdRes.ok || !createdJson.ok) {
        throw new Error(createdJson.error || "Konto konnte nicht angelegt werden");
      }
      await sendLearningSignal(corrected ? "SUGGESTION_CORRECTED" : "ACCOUNT_CREATED", {
        created: createdPayload
      });
      const item = createdJson.item as Account;
      setAccounts((prev) => [...prev.filter((x) => x.number !== item.number), item].sort((a, b) => a.number.localeCompare(b.number, "de-CH")));
      setShowCreate(false);
      setAccountForm({ number: "", name: "", type: "Expense", group: "", subgroup: "" });
      setNote("✅ Konto erstellt und Lernsignal gespeichert.");
    } catch (e) {
      setNote(e instanceof Error ? `❌ ${e.message}` : "❌ Konto-Erstellung fehlgeschlagen");
    } finally {
      setCreateBusy(false);
    }
  }

  React.useEffect(() => {
    let cancelled = false;
    const year = new Date().getFullYear();
    fetch(`/api/vat/year-settings?taxYear=${year}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((json: { ok?: boolean; setting?: { vatScheme?: "EFFEKTIV" | "PAUSCHAL" } | null }) => {
        if (cancelled) return;
        if (json.ok && json.setting?.vatScheme === "PAUSCHAL") {
          setVatMode("PAUSCHAL");
        } else {
          setVatMode("NORMAL");
        }
      })
      .catch(() => {
        if (!cancelled) setVatMode("NORMAL");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/accounts", { cache: "no-store" })
      .then((r) => r.json())
      .then((json: { ok?: boolean; items?: Account[] }) => {
        if (cancelled || !json.ok || !Array.isArray(json.items)) return;
        const map = new Map<string, Account>();
        for (const a of data) map.set(a.number, a);
        for (const a of json.items) map.set(a.number, a);
        const merged = Array.from(map.values()).sort((a, b) => a.number.localeCompare(b.number, "de-CH"));
        setAccounts(merged);
      })
      .catch(() => {
        // Keep static fallback list.
      });
    return () => {
      cancelled = true;
    };
  }, [data]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 style={{ marginBottom: 4 }}>Kontenplan</h1>
          <div style={{ color: "var(--muted)", fontSize: 13 }}>VEB/KMU Schweiz (mit Deklarations- und Berechnungstags)</div>
        </div>

        <div className="page-actions">
          <input
            className="input input-compact"
            placeholder="Suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select className="select" value={vatMode} onChange={(e) => setVatMode(e.target.value as VatMode)} title="Default-MWST im Kontenplan">
            <option value="NORMAL">MWST: Normal (Default)</option>
            <option value="PAUSCHAL">MWST: Pauschal (0%)</option>
            <option value="NICHT_STEUERPFLICHTIG">MWST: Nicht steuerpflichtig (0%)</option>
          </select>
          <button className="primary-btn" type="button" onClick={() => setShowCreate(true)}>
            Neues Konto
          </button>
        </div>
      </div>

      <AccountsTable data={filtered} vatMode={vatMode} />
      {note ? <div style={{ marginTop: 8, color: "var(--muted)" }}>{note}</div> : null}

      {showCreate ? (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "min(760px, 96vw)" }}>
            <div className="modal-head">
              <div>
                <div className="modal-title">Neues Konto</div>
                <div className="modal-sub">Kontrollierter KI-Lernmodus (kein Auto-Anlegen)</div>
              </div>
              <button className="btn btn-ghost" type="button" onClick={() => setShowCreate(false)}>
                Schliessen
              </button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <label className="field">
                  <span>Konto-Nr.</span>
                  <input className="input" value={accountForm.number} onChange={(e) => setAccountForm((s) => ({ ...s, number: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Name</span>
                  <input className="input" value={accountForm.name} onChange={(e) => setAccountForm((s) => ({ ...s, name: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Typ</span>
                  <select className="select" value={accountForm.type} onChange={(e) => setAccountForm((s) => ({ ...s, type: e.target.value as AccountType }))}>
                    <option value="Asset">Asset</option>
                    <option value="Liability">Liability</option>
                    <option value="Equity">Equity</option>
                    <option value="Revenue">Revenue</option>
                    <option value="Expense">Expense</option>
                  </select>
                </label>
                <label className="field">
                  <span>Gruppe</span>
                  <select
                    className="select"
                    value={accountForm.group}
                    onChange={(e) => setAccountForm((s) => ({ ...s, group: e.target.value }))}
                  >
                    <option value="">— Gruppe auswählen —</option>
                    {groupOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Untergruppe</span>
                  <input
                    className="input"
                    list="account-subgroup-options"
                    value={accountForm.subgroup}
                    onChange={(e) => setAccountForm((s) => ({ ...s, subgroup: e.target.value }))}
                  />
                </label>
              </div>
              <datalist id="account-subgroup-options">
                {subgroupOptions.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>

              <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, marginTop: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>KI-Vorschlag</div>
                <div style={{ color: "var(--muted)" }}>
                  Konto-Nr. <strong>{suggested.number}</strong> · Gruppe <strong>{suggested.group || "-"}</strong> · Untergruppe <strong>{accountForm.subgroup || "-"}</strong>
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button className="btn btn-primary" type="button" onClick={applySuggestion}>Vorschlag übernehmen</button>
                  <button className="btn" type="button" onClick={() => { sendLearningSignal("SUGGESTION_REJECTED"); setNote("📝 Verworfen geloggt."); }}>
                    Vorschlag verwerfen
                  </button>
                </div>
              </div>

              <div className="btn-row">
                <button className="secondary-btn" type="button" onClick={() => setShowCreate(false)}>Abbrechen</button>
                <button className="primary-btn" type="button" disabled={createBusy} onClick={createAccount}>
                  Konto erstellen
                </button>
              </div>
              <div style={{ marginTop: 6, color: "var(--muted)", fontSize: 12 }}>
                Lernstatus ist zentral unter Admin · Firma steuerbar.
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
